import { useState } from 'react';

const W = 640, H = 240, P = { t: 12, r: 12, b: 28, l: 44 };

/** Dependency-free responsive SVG grouped bar chart with keyboard-accessible tooltips. */
export default function BarChart({ labels, series, valueFormat = (v) => v }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const step = max <= 4 ? 1 : Math.pow(10, Math.floor(Math.log10(max / 4)));
  const niceMax = Math.ceil(max / 4 / step) * step * 4;
  const iw = W - P.l - P.r, ih = H - P.t - P.b;
  const groupW = iw / labels.length;
  const barW = Math.min(26, (groupW * 0.72) / series.length);
  const y = (v) => P.t + ih - (v / niceMax) * ih;
  const ticks = [0, 1, 2, 3, 4].map((k) => (niceMax / 4) * k);

  return (
    <div className="cb-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Bar chart: ${series.map((s) => s.name).join(' and ')} by period`} className="w-100">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={P.l} x2={W - P.r} y1={y(t)} y2={y(t)} stroke="#E5EAF2" />
            <text x={P.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="#6B7280">{Math.round(t)}</text>
          </g>
        ))}
        {labels.map((l, i) => {
          const gx = P.l + i * groupW + groupW / 2;
          return (
            <g key={l}>
              <text x={gx} y={H - 8} textAnchor="middle" fontSize="10" fill="#6B7280">{l}</text>
              {series.map((s, si) => {
                const bx = gx - (series.length * barW) / 2 + si * barW;
                const h = Math.max(0, ih - (y(s.values[i]) - P.t));
                return <rect key={s.name} x={bx + 1} y={y(s.values[i])} width={barW - 2} height={h} rx="3" fill={s.color} opacity={hover === null || hover === i ? 1 : 0.45} />;
              })}
              <rect
                x={P.l + i * groupW} y={P.t} width={groupW} height={ih} fill="transparent" tabIndex={0}
                aria-label={`${l}: ${series.map((s) => `${s.name} ${valueFormat(s.values[i])}`).join(', ')}`}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
              />
            </g>
          );
        })}
      </svg>
      {hover !== null && (
        <div className="cb-tooltip" role="status">
          <strong>{labels[hover]}</strong>
          {series.map((s) => (
            <div key={s.name}><span className="cb-dot" style={{ background: s.color }} />{s.name}: {valueFormat(s.values[hover])}</div>
          ))}
        </div>
      )}
      <ul className="list-inline small mt-2 mb-0">
        {series.map((s) => (
          <li key={s.name} className="list-inline-item me-3"><span className="cb-dot" style={{ background: s.color }} />{s.name}</li>
        ))}
      </ul>
    </div>
  );
}
